import { IconUser } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useUserSites } from "@/api/auth/sites";
import { SitesCard } from "@/components/organisations/sites-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/dashboard/my-sites")({
  component: RouteComponent,
  staticData: {
    title: "Your Assets",
  },
});

/**
 * User Sites page - shows sites the current user has access to
 * Uses useUserSites() which works for both admin and non-admin users
 */
function RouteComponent() {
  const { data: sitesData, isLoading, error } = useUserSites();
  const { isPlatformAdmin: isAdmin } = useAuth();

  if (error) {
    return (
      <div className="p-4 lg:p-7">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-destructive font-semibold">
            Error loading sites: {error.message}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 lg:p-7 space-y-6">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Group sites by organization
  const sitesByOrg = (sitesData || []).reduce(
    (acc, site) => {
      const orgId = site.organizationId;
      const orgName = site.organization?.name || "Unknown Organization";

      if (!acc[orgId]) {
        acc[orgId] = {
          name: orgName,
          sites: [],
        };
      }
      acc[orgId].sites.push(site);
      return acc;
    },
    {} as Record<
      string,
      { name: string; sites: NonNullable<typeof sitesData> }
    >,
  );

  const orgEntries = Object.entries(sitesByOrg);

  return (
    <div className="p-4 lg:p-7 space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Your Assets</h1>
          <Badge variant="secondary" className="gap-1">
            <IconUser className="size-3" />
            Your access only
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Only the assets you're a member of.
          {isAdmin && (
            <>
              {" "}
              To see every asset across every organization, open{" "}
              <strong className="text-foreground">Asset Management</strong>.
            </>
          )}
        </p>
      </div>

      {orgEntries.length === 0 ? (
        <SitesCard
          title="Your Assets"
          description="Assets you have access to."
          sites={[]}
          isLoading={false}
          emptyMessage="You don't have access to any assets yet. Contact your administrator to get access."
        />
      ) : (
        orgEntries.map(([orgId, { name, sites }]) => (
          <SitesCard
            key={orgId}
            title={name}
            description={`Assets you have access to at ${name}.`}
            sites={sites}
            isLoading={false}
          />
        ))
      )}
    </div>
  );
}
